import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Alert, AlertDescription } from "../ui/alert";
import { Loader2, AlertCircle, Save, X, DollarSign } from "lucide-react";
import { PlanTier, ResourceType } from "../../types/subscription";
import {
  CreatePlanTierData,
  UpdatePlanTierData,
  AdminService,
} from "../../services/adminService";
import { FeaturesEditor } from "./FeaturesEditor";

interface PlanTierFormProps {
  planTier?: PlanTier;
  onSave: (planTier: PlanTier) => void;
  onCancel: () => void;
  isLoading?: boolean;
  existingPlans?: PlanTier[];
}

// Build initial flat features: default value depends on value_type
const buildDefaultFeatures = (resourceTypes: ResourceType[]): Record<string, unknown> => {
  const f: Record<string, unknown> = {};
  resourceTypes.forEach((rt) => {
    if (rt.value_type === "flag") f[rt.code] = false;
    else if (rt.value_type === "link" || rt.value_type === "plugin") f[rt.code] = "";
    else f[rt.code] = 0; // count (default)
  });
  return f;
};

export const PlanTierForm: React.FC<PlanTierFormProps> = ({
  planTier,
  onSave,
  onCancel,
  isLoading = false,
}) => {
  const isEditing = !!planTier;

  const [resourceTypes, setResourceTypes] = useState<ResourceType[]>([]);
  const [formData, setFormData] = useState<CreatePlanTierData>({
    name: "",
    description: "",
    tier_level: 0,
    price_monthly: undefined,
    price_yearly: undefined,
    is_active: true,
    features: planTier?.features ?? {},
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  // Load resource types on mount — used to drive FeaturesEditor quota rows
  useEffect(() => {
    AdminService.getResourceTypes().then(({ data }) => {
      setResourceTypes(data);
      // When creating, initialize features with all resource type codes set to 0
      if (!isEditing) {
        setFormData((prev) => ({
          ...prev,
          features: buildDefaultFeatures(data),
        }));
      }
    });
  }, [isEditing]);

  useEffect(() => {
    if (planTier) {
      setFormData({
        name: planTier.name,
        description: planTier.description || "",
        tier_level: planTier.tier_level,
        price_monthly: planTier.price_monthly,
        price_yearly: planTier.price_yearly,
        is_active: planTier.is_active,
        features: planTier.features,
      });
    }
  }, [planTier]);

  const handleInputChange = (field: keyof CreatePlanTierData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setErrors([]);
  };

  const handleFeaturesChange = (features: Record<string, any>) => {
    setFormData((prev) => ({
      ...prev,
      features,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = AdminService.validatePlanTierData(formData);
    if (!validation.isValid) {
      setErrors(validation.errors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      let result;

      if (isEditing && planTier) {
        const updateData: UpdatePlanTierData = {
          ...formData,
          id: planTier.id,
        };
        result = await AdminService.updatePlanTier(updateData);
      } else {
        result = await AdminService.createPlanTier(formData);
      }

      if (result.error) {
        setErrors([result.error]);
      } else if (result.data) {
        onSave(result.data);
      }
    } catch (error) {
      setErrors([
        error instanceof Error ? error.message : "An unexpected error occurred",
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Save className="h-5 w-5" />
          {isEditing ? "Edit Plan Tier" : "Create New Plan Tier"}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {errors.length > 0 && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <ul className="list-disc list-inside space-y-1">
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Plan Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  placeholder="e.g., Basic, Pro, Enterprise"
                  required
                />
              </div>

              <div>
                <Label htmlFor="tier_level">Tier Level *</Label>
                <Input
                  id="tier_level"
                  type="number"
                  min="0"
                  value={formData.tier_level}
                  onChange={(e) =>
                    handleInputChange(
                      "tier_level",
                      parseInt(e.target.value) || 0
                    )
                  }
                  placeholder="0"
                  required
                />
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                  placeholder="Brief description of this plan"
                  rows={3}
                />
              </div>
            </div>

            {/* Pricing & Settings */}
            <div className="space-y-4">
              {/* Pricing Section */}
              <div className="rounded-lg border p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <DollarSign className="h-4 w-4 text-emerald-500" />
                  Pricing
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price_monthly">Monthly Price</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                      <Input
                        id="price_monthly"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price_monthly ?? ""}
                        onChange={(e) =>
                          handleInputChange(
                            "price_monthly",
                            e.target.value === "" ? undefined : parseFloat(e.target.value)
                          )
                        }
                        placeholder="0.00"
                        className="pl-7"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Leave empty for free plans</p>
                  </div>
                  <div>
                    <Label htmlFor="price_yearly">Yearly Price</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                      <Input
                        id="price_yearly"
                        type="number"
                        min="0"
                        step="0.01"
                        value={formData.price_yearly ?? ""}
                        onChange={(e) =>
                          handleInputChange(
                            "price_yearly",
                            e.target.value === "" ? undefined : parseFloat(e.target.value)
                          )
                        }
                        placeholder="0.00"
                        className="pl-7"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Annual billing discount</p>
                  </div>
                </div>
              </div>

              {/* Active Toggle */}
              <div className="flex items-center justify-between rounded-lg border p-4">
                <Label htmlFor="is_active" className="flex-1 cursor-pointer">
                  Active
                  <span className="text-sm text-muted-foreground block">
                    Whether this plan is currently active
                  </span>
                </Label>
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) =>
                    handleInputChange("is_active", checked)
                  }
                />
              </div>
            </div>
          </div>

          {/* Features Configuration */}
          <div>
            <Label className="text-lg font-semibold">
              Features Configuration *
              <span className="text-sm text-muted-foreground font-normal block">
                Configure the features and limits for this plan
              </span>
            </Label>
            <div className="mt-4">
              <FeaturesEditor
                features={formData.features}
                onChange={handleFeaturesChange}
                resourceTypes={resourceTypes}
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting || isLoading}
              className="flex-1 sm:flex-none"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {isEditing ? "Updating..." : "Creating..."}
                </>
              ) : (
                <>
                  <Save className="mr-2 h-4 w-4" />
                  {isEditing ? "Update Plan" : "Create Plan"}
                </>
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting || isLoading}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};
 